import React, {Component} from 'react';
import {connect} from 'react-redux';
import memoize from 'memoize-one';

import {Dropdown, DropdownProps, DropdownChangeParams} from 'primereact/dropdown';

import {ICAL, timezones} from '~/shared/src/utilities/icalParser';

import {__} from '~/shared/src/utilities/messageCatalog';
import {StoreState} from '~/frontend/src/store';
import User from '~/shared/src/model/User';


const defaultProps = {

    /**
     * Should the field marked if dirty.
     */
    markDirty: false as boolean,

    /**
     * The label for the field.
     */
    label: '',

    /**
     * The CSS class name(s) to add to the component.
     */
    className: '',

    inputStyle: { width: '100%' } as React.CSSProperties
};

type DefaultProps = Readonly<typeof defaultProps>;

type Props = {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    /**
     * The selected timezone.
     */
    value : string;

    /**
     * The value to determine dirty against.
     */
    originalValue? : string;

    /**
     * Callback to invoke if value has changed.
     *
     * @param {String} timezone
     */
    onChange : Function;

    style? : React.CSSProperties;
};

type StateProps = {

    /**
     * The current user.
     *
     * Will be mapped from global state.
     */
    user? : User;
};

export type ComponentProps = Omit<DropdownProps, 'onChange'> & Props & Partial<DefaultProps>;
type AllProps = Omit<DropdownProps, 'onChange'> & Props & DefaultProps & StateProps;


/**
 * Dropdown for schema selection
 */
class TimezoneDropdown extends Component<AllProps> {


    static defaultProps = defaultProps;

    /**
     * @param props
     */
    constructor( props : AllProps ) {
        super( props );

        this.getData = memoize( this.getData );

        this.onChange = this.onChange.bind( this );
    }

    getData() : { value : string }[] {
        const retVal : { value : string }[] = [];

        Object.keys( timezones ).forEach( timezone => {
            if ( ICAL.TimezoneService.has( timezone ) ) {
                retVal.push( {
                    value: timezone
                } );
            }
        } );

        return retVal;
    }

    /**
     * Handler for Dropdown change event
     *
     * @private
     */
    onChange( event : DropdownChangeParams ) {
        let value : string = '';

        if ( event.value ) {
            value = event.value;
        }

        this.props.onChange( value );
    }

    /**
     * REACT lifecycle method
     */
    render() {
        const {
                  uid,
                  value,
                  originalValue,
                  markDirty,
                  label,
                  className,
                  style,
                  inputStyle,
                  // dispatch is listed here to be excluded from dropdownProps
                  // eslint-disable-next-line @typescript-eslint/no-unused-vars
                  dispatch,
                  // onChange is listed here to be excluded from dropdownProps
                  // eslint-disable-next-line @typescript-eslint/no-unused-vars
                  onChange,
                  // user is listed here to be excluded from dropdownProps
                  // eslint-disable-next-line @typescript-eslint/no-unused-vars
                  user,
                  ...dropdownProps
              }                  = this.props as AllProps,
              data               = this.getData(),
              dropdownClassNames = [ 'yn-inputgroup' ];

        if ( !label ) {
            dropdownClassNames.push( className );
        }
        if ( markDirty && value !== originalValue ) {
            dropdownClassNames.push( 'yn-dirty-border' );
        }

        return <div className={className + ' field'} style={style}>
            {label
                ? <label
                    htmlFor={`${uid}_timezoneDropdown_i`}
                    className="block"
                >
                    {label}
                </label>
                : ''
            }
            <Dropdown
                {...dropdownProps}
                id={`${uid}_timezoneDropdown`}
                inputId={`${uid}_timezoneDropdown_i`}
                value={value}
                onChange={this.onChange}
                options={data}
                optionValue="value"
                optionLabel="value"
                filter
                filterBy="value"
                required
                className={dropdownClassNames.join( ' ' )}
                placeholder={__( 'YN.base.finder.timezone' )}
                style={inputStyle}
            />
        </div>;
    }
}

/**
 * REDUX stuff
 */
function mapStateToProps( state : StoreState, ownProps : ComponentProps ) : StateProps {
    const retVal : StateProps = {
        user: undefined
    };

    // get some session user information
    if ( state.session?.user ) {
        retVal.user = state.session.user;
    }

    return retVal;
}

// typing of the connect function makes working that the action props are defined but not required when using the component
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
export default connect<Partial<ComponentProps>, {}, ComponentProps, StoreState>( mapStateToProps )( TimezoneDropdown );
